/**
 * sceneBuilderClient
 *
 * Purpose
 * - Provide a singleton Comlink client for the SceneBuilderWorker so all
 *   scene-load requests share the same worker instance.
 * - Loads node data from OPFS in the worker; main thread then populates
 *   SceneManager Scene with the result.
 *
 * Notes
 * - The worker reads model files from OPFS and deserializes to nodes per modelId.
 * - Main thread calls populateSceneFromModelsAndNodes(models, result.nodesByModelId).
 * - Set useMainThread: true to run on the main thread instead of a worker (e.g. debugging).
 */

import type { SceneLoadResult } from '@/workers/SceneBuilderWorker';

// Lazy, on-demand singleton to avoid spawning the worker at app startup
let _workerInstance: any | undefined;
function getWorkerSingleton() {
  if (!_workerInstance) {
    _workerInstance = new ComlinkWorker<typeof import('@/workers/SceneBuilderWorker')>(
      new URL('./SceneBuilderWorker', import.meta.url),
      { name: 'SceneBuilderWorker', type: 'module' }
    );
  }
  return _workerInstance;
}

export interface SceneBuilderOptions {
  /**
   * When true, runs the load on the main thread instead of a web worker.
   * @default false
   */
  useMainThread?: boolean;
}

/**
 * Loads scene buffers from OPFS (in worker or main thread).
 * Main thread must deserialize result.buffersByModelId (e.g. with deserializeConcatenatedNodeArrays)
 * and then call sceneManager.populateSceneFromModelsAndNodes(models, nodesByModelId).
 *
 * @param modelIds   Array of model GUIDs to load from OPFS.
 * @param options    Optional: useMainThread to run on main thread.
 * @returns SceneLoadResult with buffersByModelId (transferable) and loadTimeMs.
 */
export async function loadSceneFromOPFS(
  modelIds: string[],
  options?: SceneBuilderOptions
): Promise<SceneLoadResult> {
  const useMainThread = options?.useMainThread ?? false;
  const mode = useMainThread ? 'MAIN_THREAD' : 'WEB_WORKER';
  const startTime = performance.now();

  console.log(`[sceneBuilderClient] Loading scene from OPFS (mode: ${mode}, models: ${modelIds.length})`);

  let result: SceneLoadResult;

  if (useMainThread) {
    const { loadSceneFromOPFS: loadInWorker } = await import('@/workers/SceneBuilderWorker');
    result = await loadInWorker(modelIds);
  } else {
    const worker = getWorkerSingleton();
    result = await (worker as any).loadSceneFromOPFS(modelIds);
  }

  const totalTime = performance.now() - startTime;
  const overheadTime = totalTime - result.loadTimeMs;

  console.log(`[sceneBuilderClient] Scene load complete (mode: ${mode})`);
  console.log(`[sceneBuilderClient] Load time: ${result.loadTimeMs.toFixed(2)}ms, overhead: ${overheadTime.toFixed(2)}ms, total: ${totalTime.toFixed(2)}ms`);

  return result;
}
